<script setup lang="ts">
  import { computed } from 'vue';

  import {
    buildProtheusPartyKey,
    resolveProtheusRecipientEmail,
    type ProtheusClient,
    type ProtheusRecipientType,
    type ProtheusSupplier,
  } from '@/entities/Protheus';
  import { Checkbox, TableTitle, Tooltip } from '@/shared/ui';

  import {
    getElectronicRecipientRowKey,
    isRecipientEmailInvalid,
    toElectronicRecipientRow,
    type ElectronicRecipientRow,
  } from '../model/electronicRecipient';
  import { useRecipientRowsBatch } from '../model/useRecipientRowsBatch';

  type ProtheusRecipientOption = ProtheusClient | ProtheusSupplier;

  const props = withDefaults(
    defineProps<{
      title: string;
      emptyLabel: string;
      options: ProtheusRecipientOption[];
      recipientType: ProtheusRecipientType;
      selectedRecipients: ElectronicRecipientRow[];
      isLoading?: boolean;
      groupTestId: string;
      rowTestId: string;
      selectAllTestId: string;
      totalSelectedTestId: string;
    }>(),
    {
      isLoading: false,
    }
  );

  const emit = defineEmits<{
    (e: 'update:selectedRecipients', value: ElectronicRecipientRow[]): void;
  }>();

  const selectedRecipientKeys = computed(
    () => new Set(props.selectedRecipients.map(getElectronicRecipientRowKey))
  );

  const selectedRecipientCount = computed(
    () =>
      props.options.filter((recipient) =>
        selectedRecipientKeys.value.has(buildProtheusPartyKey(recipient))
      ).length
  );

  const allSelected = computed(
    () => props.options.length > 0 && selectedRecipientCount.value === props.options.length
  );

  const isSelectAllDisabled = computed(() => props.options.length === 0);

  const isRecipientSelected = (recipientKey: string) =>
    selectedRecipientKeys.value.has(recipientKey);

  const formatRecipientEmail = (email: string) =>
    email.trim() === '' ? 'Sem e-mail cadastrado' : email;

  const toRow = (recipient: ProtheusRecipientOption) =>
    toElectronicRecipientRow(recipient, props.recipientType);

  const recipientRows = computed(() =>
    props.options.map((recipient) => {
      const email = resolveProtheusRecipientEmail(recipient, props.recipientType);

      return {
        recipient,
        recipientKey: buildProtheusPartyKey(recipient),
        email: formatRecipientEmail(email),
        hasInvalidEmail: isRecipientEmailInvalid(email),
      };
    })
  );

  const scrollTriggerId = `${props.rowTestId}-scroll-trigger`;

  const { renderedRows, hasMoreRows } = useRecipientRowsBatch(recipientRows, scrollTriggerId);

  const updateSelectedRecipients = (value: ElectronicRecipientRow[]) => {
    emit('update:selectedRecipients', value);
  };

  const toggleRecipient = (recipient: ProtheusRecipientOption) => {
    const recipientKey = buildProtheusPartyKey(recipient);

    if (isRecipientSelected(recipientKey)) {
      updateSelectedRecipients(
        props.selectedRecipients.filter(
          (item) => getElectronicRecipientRowKey(item) !== recipientKey
        )
      );
      return;
    }

    updateSelectedRecipients([...props.selectedRecipients, toRow(recipient)]);
  };

  const toggleAllRecipients = () => {
    if (allSelected.value) {
      updateSelectedRecipients([]);
      return;
    }

    const additionalRecipients = props.options
      .filter((recipient) => !selectedRecipientKeys.value.has(buildProtheusPartyKey(recipient)))
      .map(toRow);

    updateSelectedRecipients([...props.selectedRecipients, ...additionalRecipients]);
  };
</script>

<template>
  <div
    class="flex flex-col gap-4"
    :data-testid="groupTestId"
  >
    <div class="flex items-center gap-3">
      <Checkbox
        :id="selectAllTestId"
        :checked="allSelected"
        :disabled="isSelectAllDisabled"
        :data-testid="selectAllTestId"
        @toggle="toggleAllRecipients"
      />
      <span class="text-sm text-gray-07">Selecionar todos</span>
    </div>

    <div
      class="w-full max-w-3xl overflow-hidden rounded-2xl border border-gray-02 bg-white-01"
      data-testid="recipient-group-card"
    >
      <div class="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-24 overflow-y-hidden px-4 py-6 [scrollbar-gutter:stable]">
        <TableTitle :text="title" />
        <TableTitle text="Email" />
      </div>

      <div
        v-if="isLoading"
        class="px-4 py-6 text-sm text-gray-03"
      >
        Carregando destinatários...
      </div>

      <div
        v-else-if="options.length === 0"
        class="px-4 py-6 text-sm text-gray-03"
      >
        {{ emptyLabel }}
      </div>

      <ul
        v-else
        class="max-h-80 overflow-y-auto [scrollbar-gutter:stable]"
      >
        <li
          v-for="{ recipient, recipientKey, email, hasInvalidEmail } in renderedRows"
          :key="recipientKey"
          class="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] items-center gap-24 border-b border-gray-02 px-4 py-3 text-sm font-medium text-gray-07 last:border-b-0"
          :data-testid="`${rowTestId}-${recipientKey}`"
        >
          <div class="flex min-w-0 items-center gap-3">
            <label class="flex items-center">
              <Checkbox
                :id="`${rowTestId}-${recipientKey}`"
                :checked="isRecipientSelected(recipientKey)"
                :data-testid="`${rowTestId}-checkbox-${recipientKey}`"
                @toggle="toggleRecipient(recipient)"
              />
            </label>

            <span class="truncate">{{ recipient.name }}</span>
          </div>

          <Tooltip
            class="truncate"
            :class="{ 'text-red-16': hasInvalidEmail }"
            :content="email"
            :data-testid="`${rowTestId}-email-${recipientKey}`"
          >
            {{ email }}
          </Tooltip>
        </li>
        <li
          v-if="hasMoreRows"
          :id="scrollTriggerId"
          class="h-6"
          aria-hidden="true"
        />
      </ul>
    </div>

    <p
      class="text-xs text-gray-03"
      :data-testid="totalSelectedTestId"
    >
      Total de selecionados: {{ selectedRecipients.length }}
    </p>
  </div>
</template>
